import * as React from 'react';

import { Col, cn } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideBuilding, LucideShield } from 'lucide-react';

import { useInitCustomerPortalUserRoleFormFromApi } from './hooks/useInitCustomerPortalUserRoleFormFromApi';

import {
  DetailPanel,
  EmptyDetailState,
  FormFooter,
  SelectionListItem,
  SettingsContent,
  TwoPanelSelectionLayout,
} from '~/modules/settings/_components/_shared';
import {
  $cpDiscardRoleDraftChanges,
  $cpInvitationId,
  $cpSelectRole,
  $cpUserRoleForm,
  type CpUserRoleAssignment,
} from '~/modules/settings/_state/customerPortalUserForm.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

interface RoleListItem extends CpUserRoleAssignment {
  hasFacilities: boolean;
}

export const CustomerPortalUserRolesTab = () => {
  const t = useTranslations();
  const toast = useToast();
  const invitationId = useAtomValue($cpInvitationId);
  const roleForm = useAtomValue($cpUserRoleForm);
  const selectRole = useSetAtom($cpSelectRole);
  const discardRoleDraftChanges = useSetAtom($cpDiscardRoleDraftChanges);

  useInitCustomerPortalUserRoleFormFromApi(invitationId);

  const [searchValue, setSearchValue] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);

  const roleListItems: RoleListItem[] = React.useMemo(
    () =>
      roleForm.assignments.map((assignment) => ({
        ...assignment,
        hasFacilities: assignment.assignedFacilityIds.length > 0,
      })),
    [roleForm.assignments],
  );

  const filteredRoles = React.useMemo(
    () =>
      roleListItems.filter((role) =>
        role.roleName.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [roleListItems, searchValue],
  );

  const handleSelectRole = (roleId: string) => {
    if (roleForm.isDirty) {
      discardRoleDraftChanges();
    }
    selectRole(roleId);
  };

  const handleDiscard = () => {
    discardRoleDraftChanges();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      toast.success(t.customerPortal.userDetails.roles.messages.updateSuccess);
    } catch (_error) {
      toast.error(t.customerPortal.userDetails.roles.messages.updateError);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsContent size="full" className="@CustomerPortalUserRolesTab h-full">
      <TwoPanelSelectionLayout
        items={filteredRoles}
        selectedId={roleForm.selectedRoleId}
        onSelect={handleSelectRole}
        getItemId={(role) => role.roleId}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={t.customerPortal.search.placeholder}
        leftPanelWidth={240}
        renderItem={(role, isSelected) => {
          const hasNoFacilities = role.assignedFacilityIds.length === 0;

          return (
            <div className="px-2">
              <SelectionListItem
                name={role.roleName}
                tagIcon={<LucideBuilding size={14} />}
                tagCount={role.assignedFacilityIds.length}
                isSelected={isSelected}
                className={cn(hasNoFacilities && !isSelected && 'opacity-60')}
              />
            </div>
          );
        }}
        renderDetail={(role) =>
          role ? (
            <DetailPanel
              header={
                <div className="px-8 pt-5">
                  <Col className="gap-0.5">
                    <span className="text-lg font-bold text-neutral">
                      {role.roleName}
                    </span>
                  </Col>
                </div>
              }
              footer={
                <FormFooter
                  onDiscard={handleDiscard}
                  onSave={handleSave}
                  isDiscardDisabled={isSaving || !roleForm.isDirty}
                  isSaveDisabled={isSaving || !roleForm.isDirty}
                />
              }
            >
              <span className="text-sm text-neutral/50">
                {t.customerPortal.userDetails.roles.facilitiesAssigned(
                  role.assignedFacilityIds.length,
                )}
              </span>
            </DetailPanel>
          ) : (
            <EmptyDetailState
              icon={<LucideShield />}
              title={t.customerPortal.userDetails.roles.emptyState.title}
              subtitle={t.customerPortal.userDetails.roles.emptyState.subtitle}
            />
          )
        }
      />
    </SettingsContent>
  );
};
